import React, { useEffect, useMemo, useState } from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import Label from '../../components/form/Label';
import { Modal } from '../../components/ui/modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { api, apiEndpoints } from '../../utils/apiService';
import { type TableSortState, toggleSortState } from '../../utils/listOrdering';
import { filterAndSortRows, rowSearchText } from './clientTable';

interface StoreOption {
  value: number;
  label: string;
}

interface StoreGroupOption {
  id: number;
  name: string;
}

interface GroupStore {
  id: number;
  name: string;
  store_group?: number | { id: number } | null;
  store_group_id?: number | null;
}

interface OfferLocation {
  store_id: number;
  store_name?: string;
  included?: boolean;
  multilingual_addon_active: boolean;
  sms_ordering_addon_active: boolean;
  opentable_resy_addon_active: boolean;
}

interface Plan {
  id: number;
  code: string;
  name: string;
  base_price_cents: number;
  setup_fee_cents: number;
  included_minutes: number;
  overage_rate_cents_per_minute: number;
  multilingual_addon_cents: number;
  multilingual_included: boolean;
  sms_ordering_addon_cents: number;
  sms_ordering_included: boolean;
  opentable_resy_addon_cents: number;
  opentable_resy_included: boolean;
  feature_point_1?: string;
  feature_point_2?: string;
  feature_point_3?: string;
  feature_point_4?: string;
  feature_point_5?: string;
  feature_point_6?: string;
  feature_point_7?: string;
  feature_point_8?: string;
}

type OfferSortKey =
  | 'business_name'
  | 'signer'
  | 'plan'
  | 'setup_fee'
  | 'billing_start'
  | 'status';

interface ActivationOffer {
  id: number;
  token: string;
  status: string;
  business_name: string;
  signer_name: string;
  signer_email: string;
  signer_phone: string;
  setup_fee_cents: number;
  base_price_cents: number;
  included_minutes: number;
  overage_rate_cents_per_minute: number;
  currency: string;
  billing_start_date: string;
  expiration_date: string | null;
  plan_code: string;
  created_at: string;
  stripe_subscription_id?: string;
  group_token?: string | null;
  // Present for group-scoped offers (whole StoreGroup); enables the per-store
  // add-on matrix below the row.
  store_group_id?: number | null;
  checkout_mode?: string;
  plan?: any;
  per_order_rate_cents?: number;
  multilingual_addon_cents?: number;
  multilingual_addon_active?: boolean;
  multilingual_included?: boolean;
  sms_ordering_addon_cents?: number;
  sms_ordering_addon_active?: boolean;
  sms_ordering_included?: boolean;
  opentable_resy_addon_cents?: number;
  opentable_resy_addon_active?: boolean;
  opentable_resy_included?: boolean;
  custom_pricing_note?: string;
  locations?: OfferLocation[];
}

interface ActivationOffersTabProps {
  storeOptions: StoreOption[];
}

export default function ActivationOffersTab({
  storeOptions,
}: ActivationOffersTabProps) {
  const [offers, setOffers] = useState<ActivationOffer[]>([]);
  const [offerSearch, setOfferSearch] = useState('');
  const [offerSort, setOfferSort] = useState<TableSortState<OfferSortKey>>({
    key: null,
    direction: 'asc',
  });
  const visibleOffers = useMemo(
    () =>
      filterAndSortRows(
        offers,
        offerSearch,
        (offer) =>
          rowSearchText([
            offer.business_name,
            offer.signer_name,
            offer.signer_email,
            offer.signer_phone,
            offer.plan_code,
            offer.plan?.name,
            offer.status,
            offer.setup_fee_cents,
            offer.billing_start_date,
          ]),
        offerSort,
        (offer, key) => {
          if (key === 'business_name') return offer.business_name;
          if (key === 'signer') return offer.signer_name || offer.signer_email;
          if (key === 'plan') return offer.plan?.name || offer.plan_code;
          if (key === 'setup_fee') return offer.setup_fee_cents;
          if (key === 'billing_start') return offer.billing_start_date;
          return offer.status;
        },
      ),
    [offerSearch, offerSort, offers],
  );
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Inline Subpage State
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedGroupOfferId, setExpandedGroupOfferId] = useState<
    number | null
  >(null);

  // Form Fields
  const [selectedStoreId, setSelectedStoreId] = useState<number | ''>('');
  const [selectedStoreGroupId, setSelectedStoreGroupId] = useState<number | ''>(
    '',
  );
  const [storeGroups, setStoreGroups] = useState<StoreGroupOption[]>([]);
  const [groupStores, setGroupStores] = useState<GroupStore[]>([]);
  const [locationSelections, setLocationSelections] = useState<
    Record<number, OfferLocation>
  >({});
  const [businessName, setBusinessName] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerPhone, setSignerPhone] = useState('');

  // Plan Selection & Overrides State
  const [selectedPlanCodes, setSelectedPlanCodes] = useState<string[]>([]);
  const [activePlanTab, setActivePlanTab] = useState<string>('');
  const [planOverrides, setPlanOverrides] = useState<Record<string, any>>({});

  // Modals inside subpage
  const [isAddPlanOpen, setIsAddPlanOpen] = useState(false);
  const [isEditOverrideOpen, setIsEditOverrideOpen] = useState(false);

  // Override edit state (temporary binding)
  const [editingOverride, setEditingOverride] = useState<any>(null);

  const [billingStartDate, setBillingStartDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [collectFeeOnly, setCollectFeeOnly] = useState(false);

  // Start Subscription confirmation modal
  const [startSubOfferId, setStartSubOfferId] = useState<number | null>(null);
  const [startingSubscription, setStartingSubscription] = useState(false);
  const [startSubPlanCode, setStartSubPlanCode] = useState<string>('');
  const [startSubPricing, setStartSubPricing] = useState<any>(null);
  const [showStartSubPricingEdit, setShowStartSubPricingEdit] =
    useState<boolean>(false);

  const loadOffers = () => {
    setLoading(true);
    api
      .get(apiEndpoints.getBillingActivationOffers())
      .then(async (res) => {
        if (res?.ok) {
          const data = await res.json();
          setOffers(data);
        } else {
          setError('Failed to load activation offers.');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Error loading activation offers.');
        setLoading(false);
      });
  };

  const loadPlans = () => {
    api
      .get(apiEndpoints.getPlans())
      .then(async (res) => {
        if (res?.ok) {
          const data = await res.json();
          setPlans(data.filter((p: any) => p.is_active));
        }
      })
      .catch((err) => console.error(err));
  };

  const loadGroupScopeOptions = async () => {
    const groupsRequest = api
      .get(apiEndpoints.getStoreGroups())
      .then(async (response) => {
        if (!response?.ok) return;
        const payload = await response.json();
        setStoreGroups(
          Array.isArray(payload) ? payload : payload.results || [],
        );
      })
      .catch((err) => {
        console.error('Failed to load store-group options', err);
      });

    const storesRequest = api
      .get(apiEndpoints.getStoreGroupScopeStores())
      .then(async (response) => {
        if (!response?.ok) return;
        const payload = await response.json();
        setGroupStores(
          Array.isArray(payload) ? payload : payload.results || [],
        );
      })
      .catch((err) => {
        console.error('Failed to load group store options', err);
      });

    await Promise.allSettled([groupsRequest, storesRequest]);
  };

  useEffect(() => {
    loadOffers();
    loadPlans();
    loadGroupScopeOptions();
  }, []);

  // Update business details if store is pre-selected
  useEffect(() => {
    if (selectedStoreId) {
      const store = storeOptions.find((s) => s.value === selectedStoreId);
      if (store) {
        setBusinessName(store.label.split(' (')[0]);
      }
    }
  }, [selectedStoreId, storeOptions]);

  useEffect(() => {
    if (!selectedStoreGroupId) {
      setLocationSelections({});
      return;
    }
    const group = storeGroups.find(
      (candidate) => candidate.id === selectedStoreGroupId,
    );
    if (group) setBusinessName(group.name);
    const members = groupStores.filter((store) => {
      const groupValue =
        typeof store.store_group === 'object'
          ? store.store_group?.id
          : store.store_group;
      return (store.store_group_id ?? groupValue) === selectedStoreGroupId;
    });
    setLocationSelections(
      Object.fromEntries(
        members.map((store) => [
          store.id,
          {
            store_id: store.id,
            store_name: store.name,
            included: true,
            multilingual_addon_active: false,
            sms_ordering_addon_active: false,
            opentable_resy_addon_active: false,
          },
        ]),
      ),
    );
  }, [selectedStoreGroupId, storeGroups, groupStores]);

  const updateLocationSelection = (
    storeId: number,
    field: keyof Pick<
      OfferLocation,
      | 'multilingual_addon_active'
      | 'sms_ordering_addon_active'
      | 'opentable_resy_addon_active'
    >,
    checked: boolean,
  ) => {
    setLocationSelections((current) => ({
      ...current,
      [storeId]: { ...current[storeId], [field]: checked },
    }));
  };

  const updateLocationIncluded = (storeId: number, included: boolean) => {
    setLocationSelections((current) => ({
      ...current,
      [storeId]: { ...current[storeId], included },
    }));
  };

  const handleAddPlan = (code: string) => {
    if (selectedPlanCodes.includes(code)) return;
    const plan = plans.find((p) => p.code === code);
    if (!plan) return;

    const newCodes = [...selectedPlanCodes, code];
    setSelectedPlanCodes(newCodes);
    setActivePlanTab(code);
    setIsAddPlanOpen(false);

    // Initialize custom overrides with defaults
    setPlanOverrides((prev) => ({
      ...prev,
      [code]: {
        setup_fee_cents: plan.setup_fee_cents,
        base_price_cents: plan.base_price_cents,
        included_minutes: plan.included_minutes,
        overage_rate_cents_per_minute: plan.overage_rate_cents_per_minute,
        per_order_rate_cents: 0,
        multilingual_addon_cents: plan.multilingual_addon_cents,
        multilingual_included: plan.multilingual_included,
        multilingual_addon_active: false,
        sms_ordering_addon_cents: plan.sms_ordering_addon_cents,
        sms_ordering_included: plan.sms_ordering_included,
        sms_ordering_addon_active: false,
        opentable_resy_addon_cents: plan.opentable_resy_addon_cents,
        opentable_resy_included: plan.opentable_resy_included,
        opentable_resy_addon_active: false,
        custom_pricing_note: '',
      },
    }));
  };

  const handleRemovePlan = (code: string) => {
    const newCodes = selectedPlanCodes.filter((c) => c !== code);
    setSelectedPlanCodes(newCodes);

    const updatedOverrides = { ...planOverrides };
    delete updatedOverrides[code];
    setPlanOverrides(updatedOverrides);

    if (activePlanTab === code) {
      setActivePlanTab(
        newCodes.length > 0 ? newCodes[newCodes.length - 1] : '',
      );
    }
  };

  const startEditingOverride = () => {
    const current = planOverrides[activePlanTab];
    if (current) {
      setEditingOverride({
        setup_fee: (current.setup_fee_cents / 100).toFixed(2),
        base_price: (current.base_price_cents / 100).toFixed(2),
        included_minutes: current.included_minutes.toString(),
        overage_rate: (current.overage_rate_cents_per_minute / 100).toFixed(2),
        per_order_rate: (current.per_order_rate_cents / 100).toFixed(2),
        multilingual_cents: (current.multilingual_addon_cents / 100).toFixed(2),
        multilingual_active: current.multilingual_addon_active,
        sms_ordering_cents: (current.sms_ordering_addon_cents / 100).toFixed(2),
        sms_ordering_active: current.sms_ordering_addon_active,
        opentable_resy_cents: (
          current.opentable_resy_addon_cents / 100
        ).toFixed(2),
        opentable_resy_active: current.opentable_resy_addon_active,
        custom_pricing_note: current.custom_pricing_note,
      });
      setIsEditOverrideOpen(true);
    }
  };

  const saveEditingOverride = () => {
    if (!editingOverride) return;
    setPlanOverrides((prev) => ({
      ...prev,
      [activePlanTab]: {
        ...prev[activePlanTab],
        setup_fee_cents: Math.round(
          parseFloat(editingOverride.setup_fee) * 100,
        ),
        base_price_cents: Math.round(
          parseFloat(editingOverride.base_price) * 100,
        ),
        included_minutes: parseInt(editingOverride.included_minutes, 10) || 0,
        overage_rate_cents_per_minute: Math.round(
          parseFloat(editingOverride.overage_rate) * 100,
        ),
        per_order_rate_cents: Math.round(
          parseFloat(editingOverride.per_order_rate) * 100,
        ),
        multilingual_addon_cents: Math.round(
          parseFloat(editingOverride.multilingual_cents) * 100,
        ),
        multilingual_addon_active: editingOverride.multilingual_active,
        sms_ordering_addon_cents: Math.round(
          parseFloat(editingOverride.sms_ordering_cents) * 100,
        ),
        sms_ordering_addon_active: editingOverride.sms_ordering_active,
        opentable_resy_addon_cents: Math.round(
          parseFloat(editingOverride.opentable_resy_cents) * 100,
        ),
        opentable_resy_addon_active: editingOverride.opentable_resy_active,
        custom_pricing_note: editingOverride.custom_pricing_note,
      },
    }));
    setIsEditOverrideOpen(false);
    setEditingOverride(null);
  };

  const handleCreateOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPlanCodes.length === 0) {
      setError('Please add at least one plan to this offer.');
      return;
    }
    const selectedLocations = Object.values(locationSelections).filter(
      (location) => location.included !== false,
    );
    if (selectedStoreGroupId && selectedLocations.length === 0) {
      setError('Please include at least one location in this offer.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    // Determine the root setup fee based on either the first plan override or default
    const firstPlan = selectedPlanCodes[0];
    const rootSetupFeeCents =
      planOverrides[firstPlan]?.setup_fee_cents ?? 25000;

    const payload = {
      store_id: selectedStoreGroupId ? null : selectedStoreId || null,
      store_group_id: selectedStoreGroupId || null,
      locations: selectedStoreGroupId
        ? selectedLocations.map((location) => ({
            store_id: location.store_id,
            store_name: location.store_name,
            multilingual_addon_active: location.multilingual_addon_active,
            sms_ordering_addon_active: location.sms_ordering_addon_active,
            opentable_resy_addon_active: location.opentable_resy_addon_active,
          }))
        : undefined,
      business_name: businessName,
      signer_name: signerName,
      signer_email: signerEmail,
      signer_phone: signerPhone,
      setup_fee_cents: rootSetupFeeCents,
      expiration_date: expirationDate
        ? new Date(expirationDate).toISOString()
        : null,
      billing_start_date:
        !collectFeeOnly && billingStartDate
          ? new Date(billingStartDate).toISOString()
          : null,
      checkout_mode: collectFeeOnly ? 'activation_fee_only' : 'subscription',
      plan_codes: selectedPlanCodes,
      plan_overrides: planOverrides,
    };

    try {
      const res = await api.post(
        apiEndpoints.createBillingActivationOffer(),
        payload,
      );
      if (res?.ok) {
        setSuccess('Activation offer generated successfully!');
        setShowCreatePage(false);
        // Reset Form
        setSelectedStoreId('');
        setSelectedStoreGroupId('');
        setLocationSelections({});
        setBusinessName('');
        setSignerName('');
        setSignerEmail('');
        setSignerPhone('');
        setSelectedPlanCodes([]);
        setActivePlanTab('');
        setPlanOverrides({});
        setBillingStartDate('');
        setExpirationDate('');
        setCollectFeeOnly(false);
        loadOffers();
      } else {
        const errData = await res?.json().catch(() => ({}));
        setError(errData?.detail || 'Failed to create activation offer.');
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred while creating the offer.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (token: string, groupToken?: string | null) => {
    const origin = window.location.origin;
    const path = groupToken ? `/activate/${groupToken}` : `/activate/${token}`;
    navigator.clipboard.writeText(`${origin}${path}`);
    setSuccess(
      groupToken
        ? 'Grouped activation link copied to clipboard!'
        : 'Activation link copied to clipboard!',
    );
    setTimeout(() => setSuccess(''), 3000);
  };

  const fmtCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const getDisplayStatus = (offer: ActivationOffer): string => {
    if (!offer.group_token) return offer.status;
    const siblings = offers.filter((o) => o.group_token === offer.group_token);
    if (
      siblings.some(
        (o) => o.status === 'paid' || o.status === 'subscription_started',
      )
    ) {
      return siblings.some((o) => o.status === 'subscription_started')
        ? 'subscription_started'
        : 'paid';
    }
    if (siblings.every((o) => o.status === 'canceled')) return 'canceled';
    if (siblings.every((o) => o.status === 'expired')) return 'expired';
    return offer.status;
  };

  const handleStartSubscription = async (offerId: number) => {
    setStartingSubscription(true);
    try {
      const currentOffer = offers.find((o) => o.id === offerId);
      const originalPlanCode =
        currentOffer?.plan_code || currentOffer?.plan?.code || '';
      const payload: any = {};

      if (startSubPlanCode && startSubPlanCode !== originalPlanCode) {
        payload.plan_code = startSubPlanCode;
      }

      if (showStartSubPricingEdit && startSubPricing) {
        payload.plan_code = startSubPlanCode || originalPlanCode;
        payload.setup_fee_cents = Math.round(
          parseFloat(startSubPricing.setup_fee) * 100,
        );
        payload.base_price_cents = Math.round(
          parseFloat(startSubPricing.base_price) * 100,
        );
        payload.included_minutes =
          parseInt(startSubPricing.included_minutes, 10) || 0;
        payload.overage_rate_cents_per_minute = Math.round(
          parseFloat(startSubPricing.overage_rate) * 100,
        );
        payload.per_order_rate_cents = Math.round(
          parseFloat(startSubPricing.per_order_rate) * 100,
        );
        payload.multilingual_addon_cents = Math.round(
          parseFloat(startSubPricing.multilingual_cents) * 100,
        );
        payload.multilingual_addon_active =
          !!startSubPricing.multilingual_active;
        payload.multilingual_included = !!startSubPricing.multilingual_included;
        payload.sms_ordering_addon_cents = Math.round(
          parseFloat(startSubPricing.sms_ordering_cents) * 100,
        );
        payload.sms_ordering_addon_active =
          !!startSubPricing.sms_ordering_active;
        payload.sms_ordering_included = !!startSubPricing.sms_ordering_included;
        payload.opentable_resy_addon_cents = Math.round(
          parseFloat(startSubPricing.opentable_resy_cents) * 100,
        );
        payload.opentable_resy_addon_active =
          !!startSubPricing.opentable_resy_active;
        payload.opentable_resy_included =
          !!startSubPricing.opentable_resy_included;
        payload.custom_pricing_note = startSubPricing.custom_pricing_note || '';
      }

      const res = await api.post(
        apiEndpoints.startBillingActivationOfferSubscription(offerId),
        payload,
      );
      if (res?.ok) {
        setSuccess('Subscription started successfully!');
        setTimeout(() => setSuccess(''), 3000);
        // Refresh offers list
        const refreshRes = await api.get(
          apiEndpoints.getBillingActivationOffers(),
        );
        if (refreshRes?.ok) setOffers(await refreshRes.json());
      } else {
        const errData = await res?.json().catch(() => ({}));
        setError(errData?.detail || 'Failed to start subscription.');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setStartingSubscription(false);
      setStartSubOfferId(null);
    }
  };

  if (showCreatePage) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex justify-between items-center border-b border-gray-150 dark:border-gray-800 pb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Create Onboarding Offer
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Generate a custom subscription and activation package.
            </p>
          </div>
          <button
            onClick={() => setShowCreatePage(false)}
            className="px-4 py-2 border border-gray-350 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl text-sm font-semibold transition"
          >
            Back to List
          </button>
        </div>

        {error && (
          <div className="p-4 text-sm text-error-500 bg-error-50 dark:bg-error-900/20 rounded-xl border border-error-200 dark:border-error-800">
            {error}
          </div>
        )}

        <form onSubmit={handleCreateOffer} className="space-y-6">
          {/* Card 1: Store Details */}
          <ComponentCard title="Store & Signer Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Link to Existing Store (Optional)</Label>
                <select
                  value={selectedStoreId}
                  disabled={Boolean(selectedStoreGroupId)}
                  onChange={(e) => {
                    setSelectedStoreId(
                      e.target.value ? Number(e.target.value) : '',
                    );
                    if (e.target.value) setSelectedStoreGroupId('');
                  }}
                  className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-sm dark:bg-gray-900 dark:text-white"
                >
                  <option value="">-- New Store Onboarding --</option>
                  {storeOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Link to Existing Store Group (Optional)</Label>
                <select
                  value={selectedStoreGroupId}
                  onChange={(e) => {
                    setSelectedStoreGroupId(
                      e.target.value ? Number(e.target.value) : '',
                    );
                    if (e.target.value) setSelectedStoreId('');
                  }}
                  className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-sm dark:bg-gray-900 dark:text-white"
                >
                  <option value="">-- Not a group offer --</option>
                  {storeGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Business Name</Label>
                <input
                  type="text"
                  required
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g. Pizza Palace"
                  className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-sm dark:bg-gray-900 dark:text-white font-medium"
                />
              </div>
            </div>

            {selectedStoreGroupId && (
              <div className="mt-5 space-y-3">
                <div>
                  <Label>Locations Included & Add-ons</Label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Choose the locations covered by this offer, then configure
                    add-ons for each included location.
                  </p>
                </div>
                {Object.values(locationSelections).map((location) => (
                  <div
                    key={location.store_id}
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${
                      location.included !== false
                        ? 'border-brand-200 bg-brand-50/30 dark:border-brand-800 dark:bg-brand-950/20'
                        : 'border-gray-200 opacity-60 dark:border-gray-700'
                    }`}
                  >
                    <label className="flex items-center gap-2 text-sm font-medium dark:text-white">
                      <input
                        type="checkbox"
                        checked={location.included !== false}
                        onChange={(event) =>
                          updateLocationIncluded(
                            location.store_id,
                            event.target.checked,
                          )
                        }
                      />
                      {location.store_name}
                    </label>
                    <div className="flex flex-wrap gap-4 text-xs">
                      {[
                        {
                          field: 'multilingual_addon_active',
                          label: 'Multilingual',
                          included: Boolean(
                            planOverrides[activePlanTab]?.multilingual_included,
                          ),
                        },
                        {
                          field: 'sms_ordering_addon_active',
                          label: 'SMS ordering',
                          included: Boolean(
                            planOverrides[activePlanTab]?.sms_ordering_included,
                          ),
                        },
                        {
                          field: 'opentable_resy_addon_active',
                          label: 'OpenTable / Resy',
                          included: Boolean(
                            planOverrides[activePlanTab]
                              ?.opentable_resy_included,
                          ),
                        },
                      ].map(({ field, label, included }) => (
                        <label
                          key={field}
                          className="flex items-center gap-1.5"
                        >
                          <input
                            type="checkbox"
                            disabled={location.included === false || included}
                            checked={
                              included ||
                              Boolean(location[field as keyof OfferLocation])
                            }
                            onChange={(event) =>
                              updateLocationSelection(
                                location.store_id,
                                field as keyof Pick<
                                  OfferLocation,
                                  | 'multilingual_addon_active'
                                  | 'sms_ordering_addon_active'
                                  | 'opentable_resy_addon_active'
                                >,
                                event.target.checked,
                              )
                            }
                          />
                          {label}
                          {included && (
                            <span className="text-[10px] font-semibold text-brand-600">
                              Included
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <Label>Signer Name (Optional)</Label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-sm dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <Label>Signer Email (Optional)</Label>
                <input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-sm dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <Label>Signer Phone (Optional)</Label>
                <input
                  type="tel"
                  value={signerPhone}
                  onChange={(e) => setSignerPhone(e.target.value)}
                  placeholder="+15551234567"
                  className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-sm dark:bg-gray-900 dark:text-white"
                />
              </div>
            </div>
          </ComponentCard>

          {/* Card 2: Activation / Subscription Settings */}
          <ComponentCard title="Checkout Mode & Setup Fee">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="border border-slate-200 dark:border-gray-850 rounded-xl p-4 bg-white dark:bg-gray-900 shadow-sm flex items-start gap-3">
                <input
                  type="checkbox"
                  id="collectFeeOnly"
                  checked={collectFeeOnly}
                  onChange={(e) => setCollectFeeOnly(e.target.checked)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 h-4 w-4 bg-white mt-1 cursor-pointer transition"
                />
                <label
                  htmlFor="collectFeeOnly"
                  className="cursor-pointer select-none"
                >
                  <div className="text-sm font-bold text-gray-900 dark:text-white">
                    Collect activation fee only
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 leading-normal mt-0.5">
                    Charges the onboarding/setup fee today. Monthly subscription
                    starting manual action by admin.
                  </div>
                </label>
              </div>

              {!collectFeeOnly && (
                <div>
                  <Label>Billing Start Date (Optional)</Label>
                  <input
                    type="datetime-local"
                    value={billingStartDate}
                    onChange={(e) => setBillingStartDate(e.target.value)}
                    className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-sm dark:bg-gray-900 dark:text-white"
                  />
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                    When monthly billing begins. Leave blank to start manually.
                  </p>
                </div>
              )}

              <div>
                <Label>Offer Expiration Date (Optional)</Label>
                <input
                  type="datetime-local"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-sm dark:bg-gray-900 dark:text-white"
                />
              </div>
            </div>
          </ComponentCard>

          {/* Card 3: Plans Tabs Configuration */}
          <div className="app-panel rounded-[22px] border border-gray-250 dark:border-gray-850 p-5 bg-white dark:bg-gray-900 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-150 dark:border-gray-800 pb-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Subscription Plans Options
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Add multiple options to let customer select their preference
                  at checkout.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsAddPlanOpen(true)}
                className="px-3 py-1.5 bg-brand-50 hover:bg-brand-100 text-brand-700 dark:bg-brand-950/20 dark:text-brand-400 rounded-lg text-xs font-semibold shadow-theme-xs transition"
              >
                + Add Plan Option
              </button>
            </div>

            {selectedPlanCodes.length === 0 ? (
              <div className="py-10 text-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-2xl flex flex-col items-center">
                <span className="text-gray-400 mb-2">
                  No plan options added to this offer yet.
                </span>
                <button
                  type="button"
                  onClick={() => setIsAddPlanOpen(true)}
                  className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-xs font-bold transition"
                >
                  + Add First Plan Option
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Tabs bar */}
                <div className="flex flex-wrap gap-2 border-b border-gray-100 dark:border-gray-800/80 pb-2">
                  {selectedPlanCodes.map((code) => {
                    const plan = plans.find((p) => p.code === code);
                    const isActive = activePlanTab === code;
                    return (
                      <div
                        key={code}
                        className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition cursor-pointer select-none ${
                          isActive
                            ? 'bg-brand-600 text-white shadow-theme-xs'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => setActivePlanTab(code)}
                      >
                        <span>{plan?.name || code}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePlan(code);
                          }}
                          className="hover:bg-black/10 rounded-full p-0.5 transition"
                        >
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Tab content panel */}
                {activePlanTab && planOverrides[activePlanTab] && (
                  <div className="bg-slate-50/50 dark:bg-gray-900/30 border border-slate-100 dark:border-gray-800/60 rounded-2xl p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-base font-bold text-gray-900 dark:text-white">
                          Pricing Details:{' '}
                          {plans.find((p) => p.code === activePlanTab)?.name ||
                            activePlanTab}
                        </h4>
                        {planOverrides[activePlanTab].custom_pricing_note && (
                          <p className="text-xs text-brand-600 dark:text-brand-400 font-semibold mt-1">
                            Note:{' '}
                            {planOverrides[activePlanTab].custom_pricing_note}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={startEditingOverride}
                        className="px-3.5 py-1.5 border border-slate-350 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 hover:bg-slate-50 rounded-lg text-xs font-semibold shadow-sm transition"
                      >
                        Edit Plan Configuration
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-150 dark:border-gray-800">
                        <span className="block text-gray-400 mb-0.5">
                          Base Setup Fee
                        </span>
                        <span className="font-bold text-gray-800 dark:text-white text-sm">
                          {fmtCents(
                            planOverrides[activePlanTab].setup_fee_cents,
                          )}
                        </span>
                      </div>
                      <div className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-150 dark:border-gray-800">
                        <span className="block text-gray-400 mb-0.5">
                          Base Price
                        </span>
                        <span className="font-bold text-gray-800 dark:text-white text-sm">
                          {fmtCents(
                            planOverrides[activePlanTab].base_price_cents,
                          )}
                          /mo
                        </span>
                      </div>
                      <div className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-150 dark:border-gray-800">
                        <span className="block text-gray-400 mb-0.5">
                          Included Minutes
                        </span>
                        <span className="font-bold text-gray-800 dark:text-white text-sm">
                          {planOverrides[activePlanTab].included_minutes} min
                        </span>
                      </div>
                      <div className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-150 dark:border-gray-800">
                        <span className="block text-gray-400 mb-0.5">
                          Overage Rate
                        </span>
                        <span className="font-bold text-gray-800 dark:text-white text-sm">
                          {fmtCents(
                            planOverrides[activePlanTab]
                              .overage_rate_cents_per_minute,
                          )}
                          /min
                        </span>
                      </div>
                    </div>

                    {/* Add-ons Status */}
                    <div className="mt-4 border-t border-gray-200/60 dark:border-gray-800 pt-3">
                      <span className="text-[10px] font-bold text-gray-400 uppercase block mb-2">
                        Add-ons Status
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div
                          className={`p-2.5 rounded-xl border text-xs flex items-center justify-between ${
                            planOverrides[activePlanTab]
                              .multilingual_addon_active
                              ? 'bg-emerald-50/40 border-emerald-200 dark:bg-emerald-950/10'
                              : 'bg-gray-100/40 border-gray-200 dark:bg-gray-900/40'
                          }`}
                        >
                          <span>Multilingual Agent</span>
                          <span
                            className={`font-semibold ${planOverrides[activePlanTab].multilingual_addon_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}
                          >
                            {planOverrides[activePlanTab]
                              .multilingual_addon_active
                              ? `Active (${fmtCents(planOverrides[activePlanTab].multilingual_addon_cents)}/mo)`
                              : 'Inactive'}
                          </span>
                        </div>

                        <div
                          className={`p-2.5 rounded-xl border text-xs flex items-center justify-between ${
                            planOverrides[activePlanTab]
                              .sms_ordering_addon_active
                              ? 'bg-emerald-50/40 border-emerald-200 dark:bg-emerald-950/10'
                              : 'bg-gray-100/40 border-gray-200 dark:bg-gray-900/40'
                          }`}
                        >
                          <span>SMS Ordering</span>
                          <span
                            className={`font-semibold ${planOverrides[activePlanTab].sms_ordering_addon_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}
                          >
                            {planOverrides[activePlanTab]
                              .sms_ordering_addon_active
                              ? `Active (${fmtCents(planOverrides[activePlanTab].sms_ordering_addon_cents)}/mo)`
                              : 'Inactive'}
                          </span>
                        </div>

                        <div
                          className={`p-2.5 rounded-xl border text-xs flex items-center justify-between ${
                            planOverrides[activePlanTab]
                              .opentable_resy_addon_active
                              ? 'bg-emerald-50/40 border-emerald-200 dark:bg-emerald-950/10'
                              : 'bg-gray-100/40 border-gray-200 dark:bg-gray-900/40'
                          }`}
                        >
                          <span>OpenTable/Resy API</span>
                          <span
                            className={`font-semibold ${planOverrides[activePlanTab].opentable_resy_addon_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}
                          >
                            {planOverrides[activePlanTab]
                              .opentable_resy_addon_active
                              ? `Active (${fmtCents(planOverrides[activePlanTab].opentable_resy_addon_cents)}/mo)`
                              : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Pre-existing features list */}
                    {(() => {
                      const plan = plans.find((p) => p.code === activePlanTab);
                      const features = [
                        plan?.feature_point_1,
                        plan?.feature_point_2,
                        plan?.feature_point_3,
                        plan?.feature_point_4,
                        plan?.feature_point_5,
                        plan?.feature_point_6,
                        plan?.feature_point_7,
                        plan?.feature_point_8,
                      ].filter((pt) => pt);

                      if (features.length === 0) return null;

                      return (
                        <div className="mt-4 border-t border-gray-200/60 dark:border-gray-800 pt-3">
                          <span className="text-[10px] font-bold text-gray-400 uppercase block mb-2">
                            Included Plan Features (Read Only)
                          </span>
                          <ul className="list-disc pl-4 space-y-1.5 text-xs text-gray-700 dark:text-gray-300">
                            {features.map((feature, idx) => (
                              <li key={idx}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={() => setShowCreatePage(false)}
              className="px-4 py-2 border border-gray-350 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || selectedPlanCodes.length === 0}
              className="px-5 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white rounded-xl text-sm font-bold shadow-theme-xs transition transform active:scale-95"
            >
              {submitting ? 'Generating...' : 'Generate Offer Link'}
            </button>
          </div>
        </form>

        {/* Modal: Add Plan Options */}
        <Modal
          isOpen={isAddPlanOpen}
          onClose={() => setIsAddPlanOpen(false)}
          className="max-w-md p-6"
        >
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Add Subscription Plan Option
          </h3>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
            {plans
              .filter((p) => !selectedPlanCodes.includes(p.code))
              .map((p) => (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => handleAddPlan(p.code)}
                  className="w-full text-left p-3 border border-gray-200 dark:border-gray-800 hover:border-brand-500 rounded-xl transition flex justify-between items-center group bg-white dark:bg-gray-900"
                >
                  <div>
                    <span className="font-bold text-sm text-gray-900 dark:text-white group-hover:text-brand-600 transition-colors">
                      {p.name} ({p.code})
                    </span>
                    <span className="block text-[10px] text-gray-400 mt-0.5">
                      Base rate: {fmtCents(p.base_price_cents)}/mo •{' '}
                      {p.included_minutes} included mins
                    </span>
                  </div>
                  <span className="text-brand-600 font-bold text-xs">
                    + Add
                  </span>
                </button>
              ))}
            {plans.filter((p) => !selectedPlanCodes.includes(p.code)).length ===
              0 && (
              <p className="text-center text-xs text-gray-500 italic py-4">
                All active subscription plans have been added.
              </p>
            )}
          </div>
        </Modal>

        {/* Modal: Customize Plan Override Details */}
        <Modal
          isOpen={isEditOverrideOpen}
          onClose={() => setIsEditOverrideOpen(false)}
          className="max-w-lg p-6 max-h-[85vh] overflow-y-auto"
        >
          {editingOverride && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Customize Plan Details:{' '}
                {plans.find((p) => p.code === activePlanTab)?.name ||
                  activePlanTab}
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>One-Time Setup Fee ($)</Label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={editingOverride.setup_fee}
                    onChange={(e) =>
                      setEditingOverride({
                        ...editingOverride,
                        setup_fee: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white font-medium"
                  />
                </div>

                <div>
                  <Label>Base Price ($/mo)</Label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={editingOverride.base_price}
                    onChange={(e) =>
                      setEditingOverride({
                        ...editingOverride,
                        base_price: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white font-medium"
                  />
                </div>

                <div>
                  <Label>Included Minutes</Label>
                  <input
                    type="number"
                    required
                    value={editingOverride.included_minutes}
                    onChange={(e) =>
                      setEditingOverride({
                        ...editingOverride,
                        included_minutes: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <Label>Overage Rate ($/min)</Label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={editingOverride.overage_rate}
                    onChange={(e) =>
                      setEditingOverride({
                        ...editingOverride,
                        overage_rate: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <Label>Per Order Rate ($)</Label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={editingOverride.per_order_rate}
                    onChange={(e) =>
                      setEditingOverride({
                        ...editingOverride,
                        per_order_rate: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Addons Grid */}
              <div className="border-t border-gray-150 dark:border-gray-800 pt-3">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">
                  Custom Add-on Rules
                </span>
                <div className="grid grid-cols-1 gap-3">
                  {/* Multilingual */}
                  <div className="flex items-center justify-between gap-3 p-2 bg-slate-50 dark:bg-gray-900 rounded-lg border border-slate-100 dark:border-gray-800">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingOverride.multilingual_active}
                        onChange={(e) =>
                          setEditingOverride({
                            ...editingOverride,
                            multilingual_active: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-brand-600 h-4 w-4"
                      />
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">
                        Multilingual Agent
                      </span>
                    </label>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-400">Rate: $</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editingOverride.multilingual_cents}
                        onChange={(e) =>
                          setEditingOverride({
                            ...editingOverride,
                            multilingual_cents: e.target.value,
                          })
                        }
                        className="w-20 rounded bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-750 px-2 py-0.5 text-xs text-right"
                      />
                    </div>
                  </div>

                  {/* SMS Ordering */}
                  <div className="flex items-center justify-between gap-3 p-2 bg-slate-50 dark:bg-gray-900 rounded-lg border border-slate-100 dark:border-gray-800">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingOverride.sms_ordering_active}
                        onChange={(e) =>
                          setEditingOverride({
                            ...editingOverride,
                            sms_ordering_active: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-brand-600 h-4 w-4"
                      />
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">
                        SMS Ordering Addon
                      </span>
                    </label>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-400">Rate: $</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editingOverride.sms_ordering_cents}
                        onChange={(e) =>
                          setEditingOverride({
                            ...editingOverride,
                            sms_ordering_cents: e.target.value,
                          })
                        }
                        className="w-20 rounded bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-750 px-2 py-0.5 text-xs text-right"
                      />
                    </div>
                  </div>

                  {/* OpenTable Resy */}
                  <div className="flex items-center justify-between gap-3 p-2 bg-slate-50 dark:bg-gray-900 rounded-lg border border-slate-100 dark:border-gray-800">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingOverride.opentable_resy_active}
                        onChange={(e) =>
                          setEditingOverride({
                            ...editingOverride,
                            opentable_resy_active: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-brand-600 h-4 w-4"
                      />
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">
                        OpenTable/Resy Integration
                      </span>
                    </label>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-400">Rate: $</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editingOverride.opentable_resy_cents}
                        onChange={(e) =>
                          setEditingOverride({
                            ...editingOverride,
                            opentable_resy_cents: e.target.value,
                          })
                        }
                        className="w-20 rounded bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-750 px-2 py-0.5 text-xs text-right"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label>Pricing Explanation Note (Internal context)</Label>
                <textarea
                  value={editingOverride.custom_pricing_note}
                  onChange={(e) =>
                    setEditingOverride({
                      ...editingOverride,
                      custom_pricing_note: e.target.value,
                    })
                  }
                  placeholder="e.g. Negotiated customized growth package."
                  rows={2}
                  className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-150 dark:border-gray-800 mt-4">
                <button
                  type="button"
                  onClick={() => setIsEditOverrideOpen(false)}
                  className="px-4 py-2 border border-gray-350 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEditingOverride}
                  className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-xs font-bold"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Activation Offers
        </h2>
        <button
          onClick={() => {
            // Reset state
            setSelectedPlanCodes([]);
            setActivePlanTab('');
            setPlanOverrides({});
            setBusinessName('');
            setSelectedStoreId('');
            setSignerName('');
            setSignerEmail('');
            setSignerPhone('');
            setBillingStartDate('');
            setExpirationDate('');
            setCollectFeeOnly(false);
            setError('');
            setSuccess('');
            setShowCreatePage(true);
          }}
          className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-sm font-semibold shadow-theme-xs transition"
        >
          Generate Activation Link
        </button>
      </div>

      {error && (
        <div className="p-4 text-sm text-error-500 bg-error-50 dark:bg-error-900/20 rounded-xl border border-error-200 dark:border-error-800">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 text-sm text-success-500 bg-success-50 dark:bg-success-900/20 rounded-xl border border-success-200 dark:border-success-800">
          {success}
        </div>
      )}

      <ComponentCard title="Active Activation Offers">
        <div className="mb-4 max-w-md">
          <Label>Search</Label>
          <input
            type="search"
            aria-label="Search activation offers"
            value={offerSearch}
            onChange={(event) => setOfferSearch(event.target.value)}
            placeholder="Search business, signer, plan, or status"
            className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90"
          />
        </div>
        {loading ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            Loading offers...
          </div>
        ) : visibleOffers.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            {offerSearch.trim()
              ? 'No activation offers match that search.'
              : 'No activation offers found.'}
          </div>
        ) : (
          <div className="max-w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {(
                    [
                      ['business_name', 'Business Name'],
                      ['signer', 'Signer Info'],
                      ['plan', 'Plan'],
                      ['setup_fee', 'Setup Fee'],
                      ['billing_start', 'Billing Start'],
                      ['status', 'Status'],
                    ] as const
                  ).map(([key, label]) => (
                    <TableCell key={key} className="font-semibold">
                      <button
                        type="button"
                        aria-label={`Sort by ${label}`}
                        onClick={() =>
                          setOfferSort((current) =>
                            toggleSortState(current, key),
                          )
                        }
                      >
                        {label}
                        {offerSort.key === key
                          ? offerSort.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ''}
                      </button>
                    </TableCell>
                  ))}
                  <TableCell className="font-semibold text-right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleOffers.map((offer) => (
                  <React.Fragment key={offer.id}>
                    <TableRow>
                      <TableCell className="font-medium text-gray-900 dark:text-white">
                        {offer.business_name}
                        {offer.store_group_id && (
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedGroupOfferId(
                                expandedGroupOfferId === offer.id
                                  ? null
                                  : offer.id,
                              )
                            }
                            className="ml-2 text-[10px] font-semibold text-brand-600 hover:underline"
                          >
                            {expandedGroupOfferId === offer.id
                              ? 'Hide add-ons'
                              : 'Add-ons'}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {offer.signer_name && (
                          <div className="font-semibold">
                            {offer.signer_name}
                          </div>
                        )}
                        {offer.signer_email && (
                          <div className="text-gray-400">
                            {offer.signer_email}
                          </div>
                        )}
                        {offer.signer_phone && (
                          <div className="text-gray-400">
                            {offer.signer_phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="uppercase text-xs font-semibold">
                        {offer.plan_code || 'Multiple Options'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {fmtCents(offer.setup_fee_cents)}
                        {offer.checkout_mode === 'activation_fee_only' && (
                          <span className="block text-[9px] text-brand-600 font-semibold uppercase mt-0.5">
                            Setup Fee Only
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {offer.billing_start_date
                          ? new Date(offer.billing_start_date).toLocaleString()
                          : 'Start manually'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {(() => {
                          const displayStatus = getDisplayStatus(offer);
                          return (
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full font-semibold text-[10px] ${
                                displayStatus === 'subscription_started'
                                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400'
                                  : displayStatus === 'paid'
                                    ? 'bg-green-50 text-green-700 dark:bg-green-950/20 dark:text-green-400'
                                    : displayStatus === 'expired' ||
                                        displayStatus === 'canceled'
                                      ? 'bg-red-50 text-red-700 dark:bg-red-950/20 dark:text-red-400'
                                      : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950/20 dark:text-yellow-400'
                              }`}
                            >
                              {displayStatus === 'subscription_started'
                                ? 'subscribed'
                                : displayStatus}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() =>
                              copyToClipboard(offer.token, offer.group_token)
                            }
                            className="px-2.5 py-1.5 border border-slate-200 hover:bg-slate-50 dark:border-gray-800 dark:hover:bg-gray-850 rounded-lg text-xs font-bold transition flex items-center gap-1 text-slate-700 dark:text-slate-350"
                          >
                            <svg
                              className="h-3 w-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                              />
                            </svg>
                            <span>Copy Link</span>
                          </button>
                          {offer.status === 'paid' &&
                            offer.checkout_mode === 'activation_fee_only' && (
                              <button
                                onClick={() => {
                                  setStartSubOfferId(offer.id);
                                  setStartSubPlanCode(
                                    offer.plan_code || offer.plan?.code || '',
                                  );
                                  setStartSubPricing({
                                    base_price: offer.base_price_cents
                                      ? (
                                          offer.base_price_cents / 100
                                        ).toString()
                                      : '0.00',
                                    setup_fee: offer.setup_fee_cents
                                      ? (offer.setup_fee_cents / 100).toString()
                                      : '0.00',
                                    included_minutes: offer.included_minutes
                                      ? offer.included_minutes.toString()
                                      : '0',
                                    overage_rate:
                                      offer.overage_rate_cents_per_minute
                                        ? (
                                            offer.overage_rate_cents_per_minute /
                                            100
                                          ).toString()
                                        : '0.00',
                                    per_order_rate: offer.per_order_rate_cents
                                      ? (
                                          offer.per_order_rate_cents / 100
                                        ).toString()
                                      : '0.00',
                                    multilingual_cents:
                                      offer.multilingual_addon_cents
                                        ? (
                                            offer.multilingual_addon_cents / 100
                                          ).toString()
                                        : '0.00',
                                    multilingual_active:
                                      !!offer.multilingual_addon_active,
                                    multilingual_included:
                                      !!offer.multilingual_included,
                                    sms_ordering_cents:
                                      offer.sms_ordering_addon_cents
                                        ? (
                                            offer.sms_ordering_addon_cents / 100
                                          ).toString()
                                        : '0.00',
                                    sms_ordering_active:
                                      !!offer.sms_ordering_addon_active,
                                    sms_ordering_included:
                                      !!offer.sms_ordering_included,
                                    opentable_resy_cents:
                                      offer.opentable_resy_addon_cents
                                        ? (
                                            offer.opentable_resy_addon_cents /
                                            100
                                          ).toString()
                                        : '0.00',
                                    opentable_resy_active:
                                      !!offer.opentable_resy_addon_active,
                                    opentable_resy_included:
                                      !!offer.opentable_resy_included,
                                    custom_pricing_note:
                                      offer.custom_pricing_note || '',
                                  });
                                  setShowStartSubPricingEdit(false);
                                }}
                                className="px-2.5 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-xs font-bold transition flex items-center gap-1"
                              >
                                <svg
                                  className="h-3 w-3"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span>Start Subscription</span>
                              </button>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {offer.store_group_id &&
                      expandedGroupOfferId === offer.id && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-slate-50/60 p-4">
                            <div className="space-y-2">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Frozen offer locations
                              </p>
                              {(offer.locations || []).map((location) => (
                                <div
                                  key={location.store_id}
                                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                                >
                                  <span>{location.store_name}</span>
                                  <span className="text-xs text-slate-500">
                                    {[
                                      location.multilingual_addon_active &&
                                        'Multilingual',
                                      location.sms_ordering_addon_active &&
                                        'SMS ordering',
                                      location.opentable_resy_addon_active &&
                                        'OpenTable / Resy',
                                    ]
                                      .filter(Boolean)
                                      .join(', ') || 'No paid add-ons'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ComponentCard>

      {/* Modal: Confirm Start Subscription */}
      <Modal
        isOpen={startSubOfferId !== null}
        onClose={() => {
          if (!startingSubscription) setStartSubOfferId(null);
        }}
        className="max-w-lg p-6 max-h-[85vh] overflow-y-auto"
      >
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
          Start Monthly Subscription?
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          This will begin recurring billing for{' '}
          <span className="font-semibold text-gray-700 dark:text-gray-300">
            {offers.find((o) => o.id === startSubOfferId)?.business_name ||
              'this offer'}
          </span>
          .
        </p>
        {(() => {
          const selectedOffer = offers.find((o) => o.id === startSubOfferId);
          const originalPlanCode =
            selectedOffer?.plan_code || selectedOffer?.plan?.code || '';
          return selectedOffer ? (
            <div className="bg-slate-50 dark:bg-gray-800/40 border border-slate-100 dark:border-gray-800/80 rounded-xl p-4 mb-4">
              <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                User Selection (Paid Onboarding)
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block">
                    Chosen Plan:
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {plans.find((p) => p.code === originalPlanCode)?.name ||
                      selectedOffer.plan?.name ||
                      originalPlanCode ||
                      'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block">
                    Setup Fee Paid:
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {fmtCents(selectedOffer.setup_fee_cents)}
                  </span>
                </div>
                <div className="mt-1 col-span-2">
                  <span className="text-gray-500 dark:text-gray-400 block">
                    Monthly Base Price:
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {fmtCents(selectedOffer.base_price_cents)}/month
                  </span>
                </div>
              </div>
            </div>
          ) : null;
        })()}

        <div className="space-y-4 mb-6">
          <div>
            <Label>Subscription Plan</Label>
            <select
              value={startSubPlanCode}
              onChange={(e) => setStartSubPlanCode(e.target.value)}
              className="w-full mt-1.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2 text-sm dark:bg-gray-900 dark:text-white"
            >
              {plans.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              id="showStartSubPricingEdit"
              checked={showStartSubPricingEdit}
              onChange={(e) => setShowStartSubPricingEdit(e.target.checked)}
              className="rounded border-gray-300 text-brand-600 h-4 w-4"
            />
            <label
              htmlFor="showStartSubPricingEdit"
              className="text-xs font-semibold text-gray-700 dark:text-gray-300 cursor-pointer"
            >
              Edit plan details & pricing overrides
            </label>
          </div>

          {showStartSubPricingEdit && startSubPricing && (
            <div className="space-y-4 border-t border-gray-150 dark:border-gray-800 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>One-Time Setup Fee ($)</Label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={startSubPricing.setup_fee}
                    onChange={(e) =>
                      setStartSubPricing({
                        ...startSubPricing,
                        setup_fee: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white font-medium"
                  />
                </div>

                <div>
                  <Label>Base Price ($/mo)</Label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={startSubPricing.base_price}
                    onChange={(e) =>
                      setStartSubPricing({
                        ...startSubPricing,
                        base_price: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white font-medium"
                  />
                </div>

                <div>
                  <Label>Included Minutes</Label>
                  <input
                    type="number"
                    required
                    value={startSubPricing.included_minutes}
                    onChange={(e) =>
                      setStartSubPricing({
                        ...startSubPricing,
                        included_minutes: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <Label>Overage Rate ($/min)</Label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={startSubPricing.overage_rate}
                    onChange={(e) =>
                      setStartSubPricing({
                        ...startSubPricing,
                        overage_rate: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <Label>Per Order Rate ($)</Label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={startSubPricing.per_order_rate}
                    onChange={(e) =>
                      setStartSubPricing({
                        ...startSubPricing,
                        per_order_rate: e.target.value,
                      })
                    }
                    className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Addons Grid */}
              <div className="border-t border-gray-150 dark:border-gray-800 pt-3">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">
                  Custom Add-on Rules
                </span>
                <div className="grid grid-cols-1 gap-3">
                  {/* Multilingual */}
                  <div className="flex items-center justify-between gap-3 p-2 bg-slate-50 dark:bg-gray-900 rounded-lg border border-slate-100 dark:border-gray-800">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={startSubPricing.multilingual_active}
                        onChange={(e) =>
                          setStartSubPricing({
                            ...startSubPricing,
                            multilingual_active: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-brand-600 h-4 w-4"
                      />
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">
                        Multilingual Agent
                      </span>
                    </label>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-400">Rate: $</span>
                      <input
                        type="number"
                        step="0.01"
                        value={startSubPricing.multilingual_cents}
                        onChange={(e) =>
                          setStartSubPricing({
                            ...startSubPricing,
                            multilingual_cents: e.target.value,
                          })
                        }
                        className="w-20 rounded bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-750 px-2 py-0.5 text-xs text-right"
                      />
                    </div>
                  </div>

                  {/* SMS Ordering */}
                  <div className="flex items-center justify-between gap-3 p-2 bg-slate-50 dark:bg-gray-900 rounded-lg border border-slate-100 dark:border-gray-800">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={startSubPricing.sms_ordering_active}
                        onChange={(e) =>
                          setStartSubPricing({
                            ...startSubPricing,
                            sms_ordering_active: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-brand-600 h-4 w-4"
                      />
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">
                        SMS Ordering Addon
                      </span>
                    </label>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-400">Rate: $</span>
                      <input
                        type="number"
                        step="0.01"
                        value={startSubPricing.sms_ordering_cents}
                        onChange={(e) =>
                          setStartSubPricing({
                            ...startSubPricing,
                            sms_ordering_cents: e.target.value,
                          })
                        }
                        className="w-20 rounded bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-750 px-2 py-0.5 text-xs text-right"
                      />
                    </div>
                  </div>

                  {/* OpenTable Resy */}
                  <div className="flex items-center justify-between gap-3 p-2 bg-slate-50 dark:bg-gray-900 rounded-lg border border-slate-100 dark:border-gray-800">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={startSubPricing.opentable_resy_active}
                        onChange={(e) =>
                          setStartSubPricing({
                            ...startSubPricing,
                            opentable_resy_active: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-brand-600 h-4 w-4"
                      />
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">
                        OpenTable/Resy Integration
                      </span>
                    </label>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-400">Rate: $</span>
                      <input
                        type="number"
                        step="0.01"
                        value={startSubPricing.opentable_resy_cents}
                        onChange={(e) =>
                          setStartSubPricing({
                            ...startSubPricing,
                            opentable_resy_cents: e.target.value,
                          })
                        }
                        className="w-20 rounded bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-750 px-2 py-0.5 text-xs text-right"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label>Pricing Explanation Note (Internal context)</Label>
                <textarea
                  value={startSubPricing.custom_pricing_note}
                  onChange={(e) =>
                    setStartSubPricing({
                      ...startSubPricing,
                      custom_pricing_note: e.target.value,
                    })
                  }
                  placeholder="e.g. Negotiated customized growth package."
                  rows={2}
                  className="w-full mt-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-xs dark:bg-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            disabled={startingSubscription}
            onClick={() => setStartSubOfferId(null)}
            className="px-4 py-2 border border-gray-350 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl text-sm font-semibold transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={startingSubscription}
            onClick={() =>
              startSubOfferId !== null &&
              handleStartSubscription(startSubOfferId)
            }
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-sm font-semibold transition disabled:opacity-50 flex items-center gap-2"
          >
            {startingSubscription && (
              <svg
                className="animate-spin h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            )}
            {startingSubscription ? 'Starting...' : 'Start Subscription'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
